import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { is, pathOr } from 'ramda';

import { ButtonTypes, PageTypes, IconTypes, API_URL } from 'constants/Constants';
import { LoginSchema } from 'validators/validationSchemas';
import { setAuthToken, removeAuthToken } from 'localStorageService';

import Input from 'components/common/Input/Input';
import Button from 'components/common/Button/Button';
import InlineButton from 'components/common/InlineButton/InlineButton';
import { CardHeading, CardHeadingContainer, Card } from 'components/layout/Card/Card';
import { Paragraph } from 'components/layout/Typography/Typography';

import Box from 'components/layout/Box/Box';
import styles from './LoginPage.module.scss';

const s = classNames.bind(styles);

const LoginPage = ({ authenticated, onIsAuthenticatedCallback }) => {
	const history = useHistory();
	const [authLoading, setAuthLoading] = useState(false);
	const [authError, setAuthError] = useState(null);
	const formik = useFormik({
		initialValues: {
			email: '',
			password: ''
		},
		validationSchema: LoginSchema,
		onSubmit: values => {
			setAuthLoading(true);
			submitLogin(values);
		}
	});

	const submitLogin = async form => {
		setAuthError(null);

		const settings = {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(form)
		};
		try {
			const fetchResponse = await fetch(`${API_URL}/api/Auth/signin`, settings);
			const data = await fetchResponse.json();

			if (!fetchResponse.ok) {
				throw new Error(data);
			}

			setAuthToken(data);
			onIsAuthenticatedCallback();
			setAuthLoading(false);
			history.push('/');
			return data;
		} catch (error) {
			setAuthLoading(false);
			const errorMessage = is(String, pathOr(null, ['message'], error)) ? error.message : 'Could not log in';
			setAuthError(errorMessage);
			return error;
		}
	};

	const submitLogout = () => {
		removeAuthToken();
	};

	const navigationClick = to => history.push(to);

	const { getFieldProps, errors, handleSubmit, touched } = formik;
	return (
		<div className={s('container')}>
			<Card className={s('card')} padding={'4'} withShadow>
				{authenticated && <InlineButton onClickCallback={submitLogout} text={'Log out'} color={ButtonTypes.PRIMARY} />}
				{!authenticated && (
					<React.Fragment>
						<CardHeadingContainer marginBottom={'2'}>
							<CardHeading>Log in</CardHeading>
						</CardHeadingContainer>
						<form>
							<Input
								label="Username"
								type={'email'}
								autoComplete="username"
								placeholder="yours@example.com"
								error={errors.email && touched.email ? errors.email : null}
								{...getFieldProps('email')}
							/>
							<Input
								label="Password"
								type="password"
								autoComplete="current-password"
								placeholder="your password"
								error={errors.password && touched.password ? errors.password : null}
								{...getFieldProps('password')}
							/>
							{authError && <Paragraph error>{authError}</Paragraph>}
							<Box className={s('buttons')} flexDirection={'rowReverse'}>
								<Button
									loading={authLoading}
									onClickCallback={handleSubmit}
									type={'submit'}
									icon={IconTypes.LOGIN}
									text={'Log in'}
									size={ButtonTypes.MEDIUM}
									color={ButtonTypes.PRIMARY}
								/>
								<InlineButton
									loading={authLoading}
									onClickCallback={() => {
										navigationClick(PageTypes.SIGN_UP_PAGE.path);
									}}
									text={'Sign up'}
									size={ButtonTypes.MEDIUM}
									color={ButtonTypes.PRIMARY}
								/>
							</Box>
						</form>
					</React.Fragment>
				)}
			</Card>
		</div>
	);
};

LoginPage.propTypes = {
	authenticated: PropTypes.bool,
	onIsAuthenticatedCallback: PropTypes.func
};

export default LoginPage;
